import { useMsal } from "@azure/msal-react";
import { DefaultButton, Dialog, DialogFooter, DialogType, mergeStyleSets, Stack, Text, useTheme } from "@fluentui/react";
import { IExceptionTelemetry } from "@microsoft/applicationinsights-web";
import { RootState } from "app/store";
import LinkTo from "components/controls/LinkTo";
import { EValidationSeverity, IValidationMessage } from "components/controls/ValidationView/ValidationView.type";
import { saveAs } from "file-saver";
import { emitCustomEvent, Tier1To4 } from "global/Application";
import { AppDataSource } from "global/DataSources/AppDataSource";
import { MsalTokenSource } from "global/DataSources/MsalTokenSource";
import PageLoading from "global/PageLoading";
import { ELoadStatus } from "global/Reducer/statusSlice";
import { formatDate, MemoizeAsync } from "global/Utility/TSFunctions";
import { SharedValidations } from "global/Validation/DataValidation";
import { EDataValidationContext } from "global/Validation/EDataValicationContext";
import * as IXrmOptions from "model/AttributeOptionSets.type";
import { bcbs_groupconfigurationsnapshot } from "model/AttributeOptionSets.type";
import { ChangeTypePermissions, IChangeTypePermission } from "model/ChangeTypePermissions";
import * as FAM from "model/FlexibleAttributeModel";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { v4 as uuidv4 } from 'uuid';
import ValidationDialog from "views/BlueRelationship/ValidationDialog";
import { OnRenderDescriptionValidationItem } from "views/BlueRelationship/ValidationDialog/RenderValidationDescription";
import { EValidationState, IValidationModel } from "views/BlueRelationship/ValidationDialog/ValidationDialog.type";
import { compareUnderWritingReviewToWithoutDispatch, EViewType, panelButtonsStyles, subscribeCustomEvent } from ".";
import * as IXrmTypes from '../../model/CRM';
import { appInsights } from "./AppInsight";
import { ESaveType, ISaveInstruction, SaveAction } from "./dataActions";

export interface IApplicationContext {
    events: any | null;
    dataSource: AppDataSource;
    QueueError: (error: IError) => void;
    SaveAsync: (instruction: ISaveInstruction) => void;
    ChangeTypePermissions: IChangeTypePermission;
}

interface IError {
    telemetry?: IExceptionTelemetry;
    description?: string;
    error?: Error | any;
    timeStamp?: string;
    location?: string;
}


const defaultContext: IApplicationContext = {
    events: null,
    dataSource: null as any,
    QueueError: (state) => { },
    SaveAsync: (instruction) => { },
    ChangeTypePermissions: {} as any,
};

export const ApplicationContext = createContext(defaultContext);

const ApplicationContextProvider = (props: { children: React.ReactNode }) => {
    const [loading, setLoading] = useState(false);
    const Navigated = useRef(false);

    const theme = useTheme();
    const history = useHistory();
    const { instance } = useMsal();

    const [errorQueue, setErrorQueue] = useState<IError[]>([]);

    const state = useSelector((state: RootState) => state);

    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);

    // multiple sources can change state, to avoid race, ref is the latest one always.
    // for ui bindings, adn react refresh, validationState can be used
    const validation = useRef<IValidationModel<ISaveInstruction> | null>(null);
    const [validationState, setValidationState] = useState<EValidationState | null>(null);

    const dispatch = useDispatch();

    const appInsightLog = (error: any) => {

        // appInsights not setup?
        if (!appInsights)
            return;

        const telemetry: IExceptionTelemetry = {
            id: uuidv4(),
        };

        if (error instanceof Error) {
            telemetry.exception = error;

            telemetry.properties = {
                ...telemetry.properties,
                id: telemetry.id,
            };
        }
        else {
            const errorEvent = error as ErrorEvent;

            telemetry.exception = errorEvent?.error;

            telemetry.properties = {
                ...telemetry.properties,
                message: errorEvent?.message,
                id: telemetry.id,
            };
        }

        appInsights.trackException(telemetry);
        appInsights.flush();

        return telemetry;
    }

    //globalException handler
    useEffect(() => {
        const documentRemoveEventListener = subscribeCustomEvent({
            eventType: 'globalException',
            handler: (event: any) => {
                console.error(event);

                const telemetry = appInsightLog(event.detail);

                setErrorQueue(t => {
                    const _t = [...t];
                    _t.push({
                        error: event?.detail
                            ? JSON.parse(JSON.stringify(event.detail, Object.getOwnPropertyNames(event.detail)))
                            : undefined,
                        telemetry: telemetry,
                        timeStamp: (new Date()).toISOString()
                    });
                    return _t;
                });
            },
        });

        return () => {
            documentRemoveEventListener();
        }

    }, []);

    // unhandled exceptions
    useEffect(() => {

        const onErrorHandler = (event: ErrorEvent) => {

            // BCBSSC regular users have an extension that causes an exception:
            // before the code manages to finish the service cycle, another one is queued or ignored.
            // ResizeObserver loop completed with undelivered notifications.
            // ResizeObserver loop limit exceeded
            if (event.message.includes('ResizeObserver loop')) {
                event.preventDefault();
                event.stopImmediatePropagation();
                return;
            }

            console.error(event);

            const telemetry = appInsightLog(event);

            setErrorQueue(t => {
                const _t = [...t];
                _t.push({
                    error: JSON.parse(JSON.stringify(event, Object.getOwnPropertyNames(event))),
                    telemetry: telemetry,
                    timeStamp: (new Date()).toISOString(),
                    location: window.location.href,
                });
                return _t;
            });
        }

        window.addEventListener('error', onErrorHandler);

        // Unmount - Cleanup
        return () => {
            window.removeEventListener('error', onErrorHandler);
        }
    }, []);

    // useEffect(() => {
    //     // const myWorker = new Worker(new URL('src/worker', import.meta.url));
    //     // const myWorker = new Worker("/emitEventWorker.ts");
    //     const myWorker = new Worker(worker_script);

    //     myWorker.onmessage = (m) => {
    //         console.log("msg from worker: ", m.data);
    //     };
    //     myWorker.postMessage('im from main');

    //     return () => {
    //         myWorker.terminate();
    //     }
    // }, []);


    // useEffect(() => {
    //     // const myWorker = new Worker(new URL('src/worker', import.meta.url));
    //     // const myWorker = new Worker("/emitEventWorker.ts");
    //     const myWorker = new Worker(worker_script);

    //     myWorker.onmessage = (m) => {
    //         console.log("msg from worker: ", m.data);
    //     };
    //     myWorker.postMessage('im from main');

    //     return () => {
    //         myWorker.terminate();
    //     }
    // }, []);


    const SaveAsync = async (xInstructions?: ISaveInstruction) => {

        try {


            const getSnapshotsMemoizedAsync = MemoizeAsync(async () =>
                await dataSource.getGroupConfigurationSnapshotsForAccount(state.data.account?.accountid!)
            );

            const getOpportunityMemoizedAsync = MemoizeAsync(async () => {
                if (state.data.snapshot._bcbs_opportunityid_value)
                    return await dataSource.getOpportunity(state.data.snapshot._bcbs_opportunityid_value)
            });

            const getSBCsNotReadyMemoizedAsync = MemoizeAsync(async () => {
                return await dataSource.getSBCsNotReady(state.data.snapshot.bcbs_groupconfigurationsnapshotid!);
            });


            // reload associated opportunity in case it had any other changes
            let isGoldStandard = state.data.snapshot.statuscode === bcbs_groupconfigurationsnapshot.statuscode['Gold Standard(808900006)'].value;
            let isRetired = state.data.snapshot.statuscode === bcbs_groupconfigurationsnapshot.statuscode['Retired(808900001)'].value;

            // any other save requests, while we are verifying, ignore
            if (validation.current?.state === EValidationState.Validating) {
                console.log('ignoring save request, validation is running.', validation.current);

                // // we can use this to not run validation again:
                // setValidationState(structuredClone(validation.current));
                return;
            }

            // any other save requests, while we are verifying, ignore
            if (validation.current?.state === EValidationState.PendingUser) {
                console.log('ignoring save request, pending a user interaction.', validation.current);

                // // we can use this to not run validation again:
                // setValidationState(structuredClone(validation.current));
                return;
            }

            // context has no validation attached? reset everything
            // rejected? reset everything
            if (!validation.current || validation.current.state === EValidationState.Rejected)
                validation.current = {
                    state: EValidationState.Idle,
                    data: xInstructions ?? {},
                    validationMessages: [],
                }

            const saveInstructions = validation.current.data!;
            const validationMessages = validation.current.validationMessages;

            if (validation.current.state === EValidationState.Idle) {
                validation.current.state = EValidationState.Validating;
            }

            // if (saveInstructions.saveType !== ESaveType.Submit)
            //     validation.current.state = EValidationState.Fulfilled;


            // adjusting save instructions based on feedback if any:
            // this is used on second round coming over again
            if (validation.current.state === EValidationState.Fulfilled) {
                validation.current.validationMessages.forEach(eachVM => {

                    if (eachVM.id === 'gold-standard-prompt') {

                        const goldStandardPrompt = eachVM as IValidationMessage;

                        if (goldStandardPrompt?.value === "yes") {
                            saveInstructions.onSaveGenerateGoldStandardAndSQL = true;
                        }
                    }
                    else if (eachVM.id === 'update-release-prompt') {

                        const releasePrompt = eachVM as IValidationMessage;

                        if (releasePrompt?.value === "yes") {
                            saveInstructions.onSaveUpdateRelease = true;
                        }
                    }
                    else if (eachVM.id === 'force-in-progress') {

                        const releasePrompt = eachVM as IValidationMessage;

                        if (releasePrompt?.value === "yes") {
                            saveInstructions.onSaveForceStatusCodeToInProgress = true;
                        }
                    }
                });

            }

            // Not sure what else might come up. use case areas are very blurry
            // I don't really have a design for this, jerry rigging this until full use case is clear
            // and we can implement a proper validation system, what I know so far:
            // - Validation on submit, for required fields for given change type
            // - PSA + Underwriting submission needs to be rerouted through validation
            // - Underwriting (review complete) collision with other snapshots
            // - cancellation pending status to chance to committed cancellation status 
            // - cancellation fields to be populated (unconfirmed)

            // Pending:
            // - coverage level
            // - cancellation required levels and fields
            // - field validation
            // - duplicate names on entities like plans, benefits, ...


            // if submitting, we need to run the validation : 
            // we have not validated and this type of save requires validation:
            if (validation.current.state === EValidationState.Validating) {
                if (saveInstructions.saveType === ESaveType.ESubmit) {

                    setValidationState(validation.current!.state);

                    let forceUnderwritingReview = false;

                    let isDataEntryComplete = false;

                    let isAnotherPendingUW: IXrmTypes.bcbs_groupconfigurationsnapshot[] | null = null;

                    let isCancellation = false;

                    // first lets mark as data entry complete!

                    let onSaveChangeStatusCode = bcbs_groupconfigurationsnapshot.statuscode['Data Entry Complete(808900000)'].value;

                    // Cache
                    getSBCsNotReadyMemoizedAsync();

                    // when I submit a record that is MNIL, it is going to "MNIL Data Entry Complete" instead of "Data Entry Complete"
                    // const changeTypeValue = props.state.data.data.changeType?.value;
                    // changeTypeValue === bcbs_groupconfigurationsnapshot.bcbs_changetypecode["MNIL  Benefit Change Continuation(808900007)"].value ||
                    // changeTypeValue === bcbs_groupconfigurationsnapshot.bcbs_changetypecode["MNIL  Initial Setup Continuation(808900005)"].value ||
                    // changeTypeValue === bcbs_groupconfigurationsnapshot.bcbs_changetypecode["MNIL  Renewal Continuation(808900008)"].value
                    if (state?.data?.json?.MNIL?.value) {
                        onSaveChangeStatusCode = bcbs_groupconfigurationsnapshot.statuscode['MNIL Data Entry Complete(808900003)'].value;
                    }

                    isDataEntryComplete = true;

                    saveInstructions.onSaveStatusScreen = true;
                    saveInstructions.onPostSaveSetViewOnly = true;
                    saveInstructions.onSaveSetStatusCode = onSaveChangeStatusCode


                    // if its admin/psa change, then we  by pass data entry complete stage
                    if (
                        state.data.json.changeType?.value === bcbs_groupconfigurationsnapshot.bcbs_changetypecode["PSA Change(808900012)"].value ||
                        state.data.json.changeType?.value === bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Administrative Change(808900003)"].value
                    ) {
                        let onSaveChangeStatusCode = bcbs_groupconfigurationsnapshot.statuscode['Underwriting Review Complete(2)'].value;

                        isDataEntryComplete = false
                        forceUnderwritingReview = true;

                        saveInstructions.onSaveStatusScreen = true;
                        saveInstructions.onPostSaveSetViewOnly = true;
                        saveInstructions.onSaveSetStatusCode = onSaveChangeStatusCode;
                    }

                    // if its admin/psa change, then we  by pass data entry complete stage
                    if (saveInstructions.onSaveBypassUnderwritingTaskGeneration) {
                        let onSaveChangeStatusCode = bcbs_groupconfigurationsnapshot.statuscode['Underwriting Review Complete(2)'].value;

                        isDataEntryComplete = false
                        forceUnderwritingReview = true;

                        saveInstructions.onSaveStatusScreen = true;
                        saveInstructions.onPostSaveSetViewOnly = true;
                        saveInstructions.onSaveSetStatusCode = onSaveChangeStatusCode;
                    }

                    // any other snapshots with same effective date exists that is in almost final stage?
                    // lets mark this one as pending
                    if (isDataEntryComplete || forceUnderwritingReview) {

                        // TODO optimize this
                        const snapshotsMemoized = await getSnapshotsMemoizedAsync();

                        const anyUW = snapshotsMemoized.filter(eachSnapshot =>
                            (
                                eachSnapshot.statuscode === bcbs_groupconfigurationsnapshot.statuscode['Data Entry Complete(808900000)'].value ||
                                eachSnapshot.statuscode === bcbs_groupconfigurationsnapshot.statuscode['Corrections Pending(808900005)'].value
                            )
                            && new Date(eachSnapshot?.bcbs_effectivedate ?? 1).getTime() === new Date(state.data?.json?.effectiveDate?.value ?? 0).getTime()
                        );

                        if (anyUW.length > 1)
                            isAnotherPendingUW = anyUW;

                        if (anyUW.length === 1 && anyUW[0].bcbs_groupconfigurationsnapshotid !== state.data.snapshot.bcbs_groupconfigurationsnapshotid)
                            isAnotherPendingUW = anyUW;

                        if (isAnotherPendingUW) {
                            saveInstructions.onSaveSetStatusCode = bcbs_groupconfigurationsnapshot.statuscode['Pending Underwriting(808900009)'].value;

                            forceUnderwritingReview = false;
                            isDataEntryComplete = false;
                        }
                    }

                    if (saveInstructions.onSaveSetStatusCode === bcbs_groupconfigurationsnapshot.statuscode['Data Entry Complete(808900000)'].value)
                        if (state.data.json.changeType?.value !== IXrmOptions.bcbs_groupconfigurationsnapshot.bcbs_changetypecode['Initial Setup(808900002)'].value) {

                            // get me best snapshot comparable to this
                            const compareCandidate = compareUnderWritingReviewToWithoutDispatch(state, state.data.snapshot);

                            // if no default left snapshot is identified
                            // no parent is set?
                            if (!compareCandidate && !state.data.snapshot._bcbs_uwconfirmedparentid_value) {
                                // Pending Power User Review
                                saveInstructions.onSaveSetStatusCode = bcbs_groupconfigurationsnapshot.statuscode["Pending Power User Review(808900015)"].value;
                            }

                            if (compareCandidate)
                                if (!state.data.snapshot._bcbs_uwconfirmedparentid_value) {
                                    saveInstructions.onSaveChangeUWConfirmedParentId = compareCandidate.bcbs_groupconfigurationsnapshotid
                                }
                        }

                    // Cancellation Updates
                    if (state?.data?.json?.status?.value === FAM.EGroupConfigurationStatus['Cancellation in Progress']) {
                        isCancellation = true;

                        let onSaveChangeStatusCode = bcbs_groupconfigurationsnapshot.statuscode['Cancelled(808900011)'].value;

                        saveInstructions.onSaveStatusScreen = true;
                        saveInstructions.onPostSaveSetViewOnly = true;
                        saveInstructions.onSaveSetStatusCode = onSaveChangeStatusCode;
                    }

                    if (state?.data?.json?.status?.value === FAM.EGroupConfigurationStatus['Transferred to Other LOB in Progress']) {
                        isCancellation = true;

                        let onSaveChangeStatusCode = bcbs_groupconfigurationsnapshot.statuscode['Transferred to Other LOB(808900012)'].value;

                        saveInstructions.onSaveStatusScreen = true;
                        saveInstructions.onPostSaveSetViewOnly = true;
                        saveInstructions.onSaveSetStatusCode = onSaveChangeStatusCode;
                    }

                    if (isCancellation) {
                        isDataEntryComplete = false;
                        isAnotherPendingUW = null;
                        forceUnderwritingReview = false;
                    }

                    // Validation
                    // BUG 319455 - Group Configuration - Opportunity Validation
                    if (
                        state.data.snapshot.bcbs_changetypecode === bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Initial Setup(808900002)"].value ||
                        state.data.snapshot.bcbs_changetypecode === bcbs_groupconfigurationsnapshot.bcbs_changetypecode["MNIL  Renewal Continuation(808900008)"].value ||
                        state.data.snapshot.bcbs_changetypecode === bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Benefit Renewal(808900010)"].value ||
                        state.data.snapshot.bcbs_changetypecode === bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Financial Renewal(808900011)"].value ||
                        state.data.snapshot.bcbs_changetypecode === bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Comprehensive Renewal(808900001)"].value
                    ) {
                        let opportunityMemoized = await getOpportunityMemoizedAsync();

                        if (opportunityMemoized && opportunityMemoized.estimatedclosedate && state.data.json.effectiveDate?.value) {
                            const opportunityEffectiveDate = new Date(opportunityMemoized!.estimatedclosedate);
                            const gcEffectiveDate = new Date(state.data.json.effectiveDate?.value);

                            const delta = opportunityEffectiveDate.getTime() - gcEffectiveDate.getTime();

                            if (delta !== 0) {
                                const t: IValidationMessage = {
                                    title: `The Effective Date of the Group Configuration Snapshot does not match the Effective Date of the related Opportunity.`,
                                    onRenderDescription: OnRenderDescriptionValidationItem(
                                        <div>
                                            <table>
                                                <tbody>
                                                    <tr>
                                                        <td style={{ textAlign: 'right' }}>Opportunity</td>
                                                        <td><LinkTo opportunity={opportunityMemoized} /></td>
                                                    </tr>
                                                    <tr>
                                                        <td style={{ textAlign: 'right' }}>Group Configuration Effective Date</td>
                                                        <td>{formatDate(gcEffectiveDate, 'en-US-ET:yyyy-MM-ddThh:mm:ssZ as local')}</td>
                                                    </tr>
                                                    <tr>
                                                        <td style={{ textAlign: 'right' }}>Related Opportunity Effective Date</td>
                                                        <td>{formatDate(opportunityEffectiveDate, 'en-US-ET:yyyy-MM-ddThh:mm:ssZ as local')}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    ),
                                    type: EValidationSeverity.error
                                };

                                // show warning for Financial Renewal
                                // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/424549
                                if (state.data.snapshot.bcbs_changetypecode === bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Financial Renewal(808900011)"].value)
                                    t.type = EValidationSeverity.warn


                                validationMessages.push(t);
                            }
                        }
                    }

                    // 319457 - Initial Setup and Renewal Validation
                    //  if there exists a Group Configuration with the same Change Type and Effective Date and status is Data Entry Complete, Corrections Pending, Gold Standard, or Retired,
                    if (
                        state.data.snapshot.bcbs_changetypecode === bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Initial Setup(808900002)"].value ||
                        state.data.snapshot.bcbs_changetypecode === bcbs_groupconfigurationsnapshot.bcbs_changetypecode["MNIL  Renewal Continuation(808900008)"].value ||
                        state.data.snapshot.bcbs_changetypecode === bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Benefit Renewal(808900010)"].value ||
                        state.data.snapshot.bcbs_changetypecode === bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Financial Renewal(808900011)"].value ||
                        state.data.snapshot.bcbs_changetypecode === bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Comprehensive Renewal(808900001)"].value
                    ) {

                        const snapshotsMemoized = await getSnapshotsMemoizedAsync();

                        const matchingSnapshots = snapshotsMemoized.filter(t => {

                            // same snapshot as currently loaded one?
                            if (t.bcbs_groupconfigurationsnapshotid === state.data.snapshot.bcbs_groupconfigurationsnapshotid)
                                return false;

                            // Condition - not a Data Entry Complete/Gold Standard/Retired
                            const status =
                                t.statuscode === bcbs_groupconfigurationsnapshot.statuscode['Data Entry Complete(808900000)'].value ||
                                t.statuscode === bcbs_groupconfigurationsnapshot.statuscode['Gold Standard(808900006)'].value ||
                                t.statuscode === bcbs_groupconfigurationsnapshot.statuscode['Retired(808900001)'].value;

                            if (!status)
                                return false;

                            // Condition - effective date miss match
                            const effectiveDateMatch = t.bcbs_effectivedate === state.data.snapshot.bcbs_effectivedate;
                            if (!effectiveDateMatch)
                                return false;

                            // Condition - Change Type
                            let expectedChangeType = false;
                            if (state.data.snapshot.bcbs_changetypecode === bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Initial Setup(808900002)"].value) {

                                expectedChangeType =
                                    t.bcbs_changetypecode === bcbs_groupconfigurationsnapshot.bcbs_changetypecode['Initial Setup(808900002)'].value;

                            } else if (state.data.snapshot.bcbs_changetypecode === bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Benefit Renewal(808900010)"].value) {

                                expectedChangeType =
                                    t.bcbs_changetypecode === bcbs_groupconfigurationsnapshot.bcbs_changetypecode['Benefit Renewal(808900010)'].value ||
                                    t.bcbs_changetypecode === bcbs_groupconfigurationsnapshot.bcbs_changetypecode['Comprehensive Renewal(808900001)'].value;

                            } else if (state.data.snapshot.bcbs_changetypecode === bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Financial Renewal(808900011)"].value) {

                                expectedChangeType =
                                    t.bcbs_changetypecode === bcbs_groupconfigurationsnapshot.bcbs_changetypecode['Financial Renewal(808900011)'].value ||
                                    t.bcbs_changetypecode === bcbs_groupconfigurationsnapshot.bcbs_changetypecode['Comprehensive Renewal(808900001)'].value;

                            } else if (state.data.snapshot.bcbs_changetypecode === bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Comprehensive Renewal(808900001)"].value) {

                                expectedChangeType = t.bcbs_changetypecode === bcbs_groupconfigurationsnapshot.bcbs_changetypecode['Benefit Renewal(808900010)'].value ||
                                    t.bcbs_changetypecode === bcbs_groupconfigurationsnapshot.bcbs_changetypecode['Financial Renewal(808900011)'].value ||
                                    t.bcbs_changetypecode === bcbs_groupconfigurationsnapshot.bcbs_changetypecode['Comprehensive Renewal(808900001)'].value;

                            }

                            if (expectedChangeType)
                                return true;

                            return false;
                        });

                        if (matchingSnapshots && matchingSnapshots.length > 0) {
                            matchingSnapshots.forEach(snapshot => {
                                validationMessages.push({
                                    title: `A '${bcbs_groupconfigurationsnapshot.bcbs_changetypecode[snapshot.bcbs_changetypecode as keyof typeof bcbs_groupconfigurationsnapshot.bcbs_changetypecode]?.name}' Group Configuration Snapshot with the same effective date already exists.`,

                                    onRenderDescription: OnRenderDescriptionValidationItem(
                                        <>
                                            Another snapshot (<LinkTo snapshot={snapshot} />) with same effective date ({formatDate(new Date(snapshot.bcbs_effectivedate!), 'en-US-ET:yyyy-MM-ddThh:mm:ssZ as local')}) and a status of Gold Standard/Data Entry Complete/Retired was found that had a conflicting change type with current snapshot.
                                            <br />
                                            You may consider upgrading change type.
                                        </>
                                    ),
                                    type: EValidationSeverity.error
                                });
                            });
                        }
                    }

                    // Flow/State/Status Validation
                    // ----------------------------------------------------------------------------------------------
                    if (isAnotherPendingUW) {
                        isAnotherPendingUW.forEach(snapshot => {

                            validationMessages.push({
                                title: 'Another snapshot is currently with Underwriting. Submitting this snapshot will move it to Pending Underwriting status.',
                                onRenderDescription: OnRenderDescriptionValidationItem(
                                    <div>
                                        Following snapshot is with underwriting (<LinkTo snapshot={snapshot} />)
                                    </div>
                                ),
                                type: EValidationSeverity.warn
                            });
                        });
                    }

                    if (isCancellation) {
                        validationMessages.push({
                            title: `This will close the record. Please ensure all required fields (Cancellation Date, Run out Date, etc) are complete before you continue as you will not be able to update the record afterwards.`,
                            type: EValidationSeverity.warn
                        });
                    }

                    if (forceUnderwritingReview) {
                        validationMessages.push({
                            title: `This Group Configuration will be set to Underwriting Review Complete status.`,
                            description: `Current save will result in Group Configuration to be set to Underwriting Review Complete status.\nThis action cannot be undone.`,
                            type: EValidationSeverity.warn
                        });
                    }

                    if (isDataEntryComplete) {
                        validationMessages.push({
                            title: `You are submitting this snapshot for review.`,
                            description: `Data entry has been complete.`,
                            type: EValidationSeverity.info
                        });
                    }

                    // // Disabling this rule, since this is no longer needed
                    // // MUal: We need to disable that as part of the deployment of the new functionality , the one HVu has been working on.
                    // // note: currently only applicable to prod until we take this out
                    // if (Constants.env === 'prod')
                    //     if (state.data.snapshot._bcbs_groupcollectfinancialconfigurationid_value) {
                    //         validationMessages.push({
                    //             title: `Group Collect generated Group Configuration cannot be submitted to underwriting.`,
                    //             description: `A Group Configuration generated from Group Collect cannot be submitted directly to underwriting. Merge the Financial information into an existing Group Configuration to continue.`,
                    //             severity: EValidationSeverity.error
                    //         });
                    //     }

                    // BUG 301304 - Validate Opportunity on Initial Setup and Renewal
                    const opportunityRequiredChanges = [
                        bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Initial Setup(808900002)"].value,
                        bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Comprehensive Renewal(808900001)"].value,
                        bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Benefit Renewal(808900010)"].value,
                        bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Financial Renewal(808900011)"].value,
                    ];

                    if (state.data.json.changeType?.value)
                        if (opportunityRequiredChanges.includes(state.data.json.changeType.value!))
                            if (!state.data.snapshot._bcbs_opportunityid_value && !state.data.json.opportunity) {
                                validationMessages.push({
                                    title: `For all Initial Setups and Renewals, an opportunity must be identified before a Group Configuration may be submitted.`,
                                    description: `An opportunity must be associated with this Group Configuration Snapshot before it can be submitted to underwriting.`,
                                    type: EValidationSeverity.error
                                });
                            }

                    if (saveInstructions.onSaveBypassUnderwritingTaskGeneration)
                        validationMessages.push({
                            title: `This Group Configuration will not generate any tasks.`,
                            description: `This will progress the current Group Configuration to Underwriting Review Complete and prevent the generation of any tasks.  Do you wish to continue?`,
                            type: EValidationSeverity.warn
                        });


                    if (saveInstructions.onSaveSetStatusCode === bcbs_groupconfigurationsnapshot.statuscode["Pending Power User Review(808900015)"].value)
                        validationMessages.push({
                            title: `Submitting will assign this to the Group Library team for review.`,
                            description: `A default parent cannot be identified for this Group Configuration.\nSubmitting will assign this Group Configuration to the Group Library team for review to identify the default parent.`,
                            type: EValidationSeverity.warn
                        });

                    state.data.json.benefits.forEach(eachBenefit => {
                        eachBenefit.tiers.forEach(eachBenefitTier => {
                            if (eachBenefitTier.name === null || eachBenefitTier.name === '' || Tier1To4.includes(eachBenefitTier.name!)) {
                                validationMessages.push({
                                    title: `Benefit (${eachBenefit.name}) tier has indistinct name (${eachBenefitTier.name}).`,
                                    description: `Benefit tiers should be named distinctively and different.\nTo rename tier name, use benefit properties screen.\nSelect tier and apply correct naming and make sure attributes are correctly renamed and contain correct data.`,
                                    type: EValidationSeverity.warn,
                                });
                            }
                        });
                    });


                    const openRevisionsResult = await dataSource.fetchOpenRevisionTasks(
                        state.data.json.revisionTasks,
                        state.data.json.effectiveDate?.value!,
                        state.data.snapshot._bcbs_groupid_value!,
                    );

                    if (openRevisionsResult.length > 0) {
                        openRevisionsResult.forEach(eachRV => {
                            validationMessages.push({
                                title: `There are open revision tasks which must be resolved prior to submission.`,
                                type: EValidationSeverity.error,
                                onRenderDescription: OnRenderDescriptionValidationItem(
                                    <div>
                                        <table>
                                            <tr>
                                                <td style={{ textAlign: 'right' }}>Task:</td>
                                                <td><LinkTo revisionTask={eachRV} /></td>
                                            </tr>
                                        </table>
                                    </div>
                                ),
                            });
                        })
                    }

                    // Data Validation
                    // ----------------------------------------------------------------------------------------------
                    const dataValidationResult = SharedValidations(
                        state,
                        {
                            checkRef: true,
                            checkUniqueName: true,
                            checkCoverage: true,
                            checkGrandfathered: true,
                            checkPlanGroupStructure: true,
                            checkBillingOnlyPlan: true,
                            checkRelease: true,
                            checkRequiredFields: true,
                            checkData: true,
                        },
                        EDataValidationContext.ESaveSubmit,
                        history
                    );

                    if (dataValidationResult.refValidations.length > 0) {
                        validationMessages.push({
                            title: `Some elements are referencing a resource that has been deactivated or no longer exists.`,
                            description: `The occurrence of such a situation may arise when a resource undergoes deactivation during the course of business processing or fails to be selected for retention in the subsequent iteration of the merge process. In such cases, it is imperative to reassign or clear each individual entry.`,
                            type: EValidationSeverity.error,
                            inner: dataValidationResult.refValidations
                        });
                    }

                    if (dataValidationResult.uniqueEntityNameValidations.length > 0) {
                        const t = {
                            title: `Some elements are sharing an identifier within the same context.`,
                            description: `The following elements have been flagged due to naming errors, necessitating the assignment of unique names. To make the necessary changes, please navigate to the Properties section.\nHere is the list of impacted elements:`,
                            type: EValidationSeverity.info,
                            inner: dataValidationResult.uniqueEntityNameValidations
                        };

                        validationMessages.push(t);

                        if (dataValidationResult.uniqueEntityNameValidations.some(t => t.type === EValidationSeverity.error)) {
                            t.type = EValidationSeverity.error;
                        }
                    }

                    if (dataValidationResult.coverageValidations.length > 0) {
                        validationMessages.push({
                            title: 'Coverage mismatch has been detected.',
                            type: EValidationSeverity.error,
                            inner: dataValidationResult.coverageValidations
                        });
                    }

                    if (dataValidationResult.grandFatherValidations.length > 0) {
                        validationMessages.push({
                            title: 'Grandfathered/Non-Grandfathered mismatch has been detected.',
                            type: EValidationSeverity.error,
                            inner: dataValidationResult.grandFatherValidations
                        });
                    }

                    if (dataValidationResult.planGroupStructureValidations.length > 0) {
                        validationMessages.push(...dataValidationResult.planGroupStructureValidations);
                    }

                    if (dataValidationResult.billingOnlyPlanValidations.length > 0) {
                        validationMessages.push(...dataValidationResult.billingOnlyPlanValidations);
                    }

                    if (dataValidationResult.releaseValidations)
                        validationMessages.push(...dataValidationResult.releaseValidations);

                    if (dataValidationResult.relatedToGroupNumberValidations)
                        validationMessages.push(...dataValidationResult.relatedToGroupNumberValidations);

                    if (dataValidationResult.requiredFieldsValidations)
                        validationMessages.push(...dataValidationResult.requiredFieldsValidations);

                    if (dataValidationResult.dataValidations)
                        validationMessages.push(...dataValidationResult.dataValidations);


                    // Validate Related SBCs on Submit
                    // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/436940
                    // 1) Record State (statecode): Active
                    // 2) Status (bcbs_sbctype): Anything other than Ready
                    // 3) Manually Edited PDF (bcbs_manuallyeditedpdf): Not Yes 
                    const openSBCListMemoized = await getSBCsNotReadyMemoizedAsync();

                    if (validation.current.state === EValidationState.Validating)
                        if (openSBCListMemoized.length > 0)
                            validationMessages.push({
                                title: `There are related SBC records which are not ready.`,
                                onRenderDescription: OnRenderDescriptionValidationItem(
                                    <Stack tokens={{ childrenGap: '15px' }}>
                                        <Text>
                                            We have identified the following SBCs for the account that align with the specified criteria: active status, type not designated as ready, and Manually Edited PDF not marked as yes.
                                        </Text>
                                        <div>
                                            {openSBCListMemoized.map((sbc, ix) => {
                                                return <div key={sbc.bcbs_sbcid}>
                                                    <LinkTo sbc={sbc} /> {sbc._bcbs_group_name_value !== state.data.snapshot.bcbs_groupconfigurationsnapshotid ? "⚠ (different snapshot)" : ""}
                                                </div>
                                            })}
                                        </div>
                                    </Stack>
                                ),
                                type: EValidationSeverity.error,
                            });

                }
            }

            // TODO add retired here to be able to regen 
            // Gold Standard Modification Prompt
            if (validation.current.state === EValidationState.Validating)
                if (isGoldStandard || isRetired) {

                    // TODO id-message tracking for azure logs
                    const test: IValidationMessage = {
                        id: `gold-standard-prompt`,
                        title: `Update related data and processes.`,
                        description: `Modifying a gold standard/retired record directly will bypass data generation.\nEnable this option to reflect latest changes, update reports, and potentially trigger down stream processes.`,
                        type: EValidationSeverity.promptBoolean,
                    };

                    validationMessages.push(test);
                }

            // Admin Mode Prompt
            if (validation.current.state === EValidationState.Validating)
                if (state.status.debug.admin)
                    validationMessages.push({
                        title: `(Admin) mode is on.`,
                        description: `When using admin mode you can continue with saving the data.\nValidations can not prevent bad data submission.`,
                        type: EValidationSeverity.warn,
                    });

            // Force Update Release
            if (validation.current.state === EValidationState.Validating)
                if (state.status.debug.admin) {

                    // TODO id-message tracking for azure logs
                    const t: IValidationMessage = {
                        id: `update-release-prompt`,
                        title: `(Admin) Update release information on snapshot`,
                        description: `checking this will update the referenced release record to the latest published version.`,
                        type: EValidationSeverity.promptBoolean,
                    };

                    validationMessages.push(t);
                }

            if (validation.current.state === EValidationState.Validating)
                if (state.status.debug.admin) {

                    // TODO id-message tracking for azure logs
                    const t: IValidationMessage = {
                        id: `force-in-progress`,
                        title: `(Admin) Reopen as in-progress`,
                        description: `Reopen as in-progress on save`,
                        type: EValidationSeverity.promptBoolean,
                    };

                    validationMessages.push(t);
                }

            // Live Load Check
            if (validation.current.state === EValidationState.Validating)
                if (state.status.debug.liveload)
                    validationMessages.push({
                        title: `Live Configuration Loading - Ensure Accuracy of Updates.`,
                        description: `You are currently live loading the configuration.\nPlease note that saving data at this point may result in unpredictable behavior. To ensure your updates are applied correctly, we recommend reloading the page`,
                        type: EValidationSeverity.error,
                    });


            // context visibility notification, to make sure user know changed data elements are not reflected
            if (validation.current.state === EValidationState.Validating)
                if (state.status.debug.noVisibility)
                    validationMessages.push({
                        title: `Context visibility has been ignored.`,
                        type: EValidationSeverity.error,
                    });


            // Sorting
            // ---------------------------------
            validationMessages.sort((b, a) => { return b.type - a.type });

            // Results
            // ---------------------------------
            if (validation.current.validationMessages.some(q => !q.resolved))
                validation.current.state = EValidationState.PendingUser;
            else if (validation.current.state !== EValidationState.Rejected)
                validation.current.state = EValidationState.Fulfilled;

            setValidationState(validation.current!.state);

            // validation has been complete or non has been performed:
            if (validation.current.state === EValidationState.Fulfilled) {
                dispatch(SaveAction(
                    dataSource,
                    history,
                    saveInstructions
                ));
            }

            // if final state, reset everything
            if (validation.current.state === EValidationState.Fulfilled || validation.current?.state === EValidationState.Rejected)
                validation.current = null;

        } catch (error: any) {
            emitCustomEvent({
                eventType: 'globalException',
                details: error
            });
            console.error(error);

            setValidationState(null);
            validation.current = null;
        } finally {

        }
    }

    const onQueueError = (item: IError) => {
        setErrorQueue(t => {
            const _t = [...t];
            _t.push(item);
            return _t;
        });
    }

    const context: IApplicationContext = {
        ...defaultContext,
        dataSource: dataSource,
        QueueError: onQueueError,
        SaveAsync: SaveAsync,
        ChangeTypePermissions: !state.status.debug.admin
            ? ChangeTypePermissions[state.data.json.changeType?.value!]
            : ChangeTypePermissions[1],
    };


    // handle what happens on key press
    const [menuCoordinates, setMenuCoordinates] = useState<any | null>(null);

    // ctrl+s save
    useEffect(() => {

        const documentOnKeyDown = (e: any) => {
            if ((window.navigator.platform.match("Mac") ? e.metaKey : e.ctrlKey) && e.keyCode === 83) {
                // to prevent browsers default action for page save
                e.preventDefault();

                // focus out, giving slice for data to commit
                if (document.activeElement)
                    //@ts-ignore
                    document.activeElement?.blur?.();
            }
        };

        document.addEventListener("keydown", documentOnKeyDown, false);

        const documentOnKeyUp = (event: KeyboardEvent) => {

            if ((window.navigator.platform.match("Mac") ? event.metaKey : event.ctrlKey) && event.keyCode === 83) {
                event.preventDefault();

                // if already saving ?
                if (state.status.dataSavingStatus === ELoadStatus.criticalSaving)
                    return;

                console.log('context, servicing shortcut (ctrl+s)');
                setTimeout(() => {
                    SaveAsync({ saveType: ESaveType.EAuto });
                }, 100);
            };
        }

        document.addEventListener("keyup", documentOnKeyUp, false);

        // document.addEventListener("mousedown", (ev) => {
        //     if (ev.ctrlKey)
        //         setMenuCoordinates({ x: ev.x, y: ev.y });
        // });
        // document.addEventListener("mouseup", (ev) => {

        //     setMenuCoordinates(null);
        // });

        return () => {
            // clear (CTRL + S)
            document.removeEventListener("keyup", documentOnKeyUp);
            document.removeEventListener("keydown", documentOnKeyDown);
        }

    }, []);

    const windowBeforeUnload = useCallback((event: BeforeUnloadEvent) => {

        let eject = false;

        // if not modifying ignore this check
        if (state.status.viewType !== EViewType.Modify)
            eject = true;

        // const saveTokenRef = context.saveInfo.saveTokenMut
        //     ? context.saveInfo.saveTokenMut()
        //     : undefined;

        // const storeTokenRef = context.saveInfo.storeTokenMut
        //     ? context.saveInfo.storeTokenMut()
        //     : undefined;

        // if ((saveTokenRef?.current ?? 0) > (storeTokenRef?.current ?? 0))
        //     return;

        if ((state.data.savedOn ?? 0) > (state.data.modifiedOn ?? -1)) {
            // dirty changes?
            eject = true;
        }

        if (state.status.dataSavingStatus !== ELoadStatus.idle && state.status.dataSavingStatus !== ELoadStatus.complete)
            eject = false;

        if (!eject) {
            event.preventDefault();
            event.returnValue = "Are you sure you want to quit?"
            return event.returnValue;
        }
    }, [state]);

    // unload event
    useEffect(() => {
        window.addEventListener('beforeunload', windowBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', windowBeforeUnload);
        }
    }, [windowBeforeUnload]);

    // auto save
    useEffect(() => {
        const MINUTE_MS = 5 * 60 * 1000;

        const interval = setInterval(() => {

            if (state.status.viewType !== EViewType.Modify) {
                console.log('[appl] Context.interval', "autosave-eject-viewOnly");
                return;
            }

            if (state?.status?.debug?.autoSaveOff) {
                console.log('[appl] Context.interval', "autosave-eject-debug");
                return;
            }
            // if already saving ?
            if (state.status.dataSavingStatus !== ELoadStatus.idle) {
                console.log('[appl] Context.interval', "autosave-eject-pending");
                return;
            }

            // TODO add a check for last save
            console.log('[appl] Context.interval', "autosave");
            SaveAsync({ saveType: ESaveType.EAuto });

        }, MINUTE_MS);

        return () => {
            clearInterval(interval);
        }
    }, []);

    const contentStyles = useMemo(() => {
        return mergeStyleSets({
            container: {
                width: '600px',
                minWidth: '600px'
            }
        });
    }, []);

    const lastError = errorQueue?.[0];

    const onRenderErrorDialog = () => {
        return <Dialog
            minWidth={600}
            hidden={errorQueue.length === 0}
            onDismiss={() => setErrorQueue([])}
            dialogContentProps={{
                type: DialogType.largeHeader,
                title: 'Uh oh! Something went wrong...',
                subText: `Try this action again. If the problem continues, contact your organization's Group Library Administrator.`,
            }}
            modalProps={{
                isBlocking: false,
                containerClassName: contentStyles.container
            }}
        >
            <>
                {lastError?.error?.description &&
                    <>
                        <Text>{lastError.error.description}</Text>
                        <br />
                    </>
                }

                {lastError && lastError.error &&
                    <div style={{
                        boxSizing: 'border-box',
                        backgroundColor: theme.semanticColors.bodyBackground,
                        border: '1px solid rgb(202, 202, 202)',
                        width: '100%',
                        fontSize: 'x-small',
                        maxHeight: '12em',
                        overflowY: 'scroll',
                        whiteSpace: 'pre-wrap',
                        color: theme.semanticColors.bodyText,
                    }}>
                        <code>

                            {lastError.timeStamp &&
                                <div>Timestamp: {lastError.timeStamp}</div>
                            }

                            {lastError.telemetry?.id &&
                                <div>Insight Tracker: {lastError.telemetry.id}</div>
                            }

                            {!lastError.description &&
                                <p>
                                    {lastError.description}
                                </p>
                            }
                            <div>

                                {lastError.error.message &&
                                    <div>
                                        {lastError.error.message}
                                    </div>
                                }
                                {lastError.error.stack &&
                                    <div>
                                        {lastError.error.stack}
                                    </div>
                                }
                            </div>
                        </code>
                    </div>
                }
            </>


            <DialogFooter>
                <DefaultButton text="Download Log"
                    styles={panelButtonsStyles}
                    onClick={() => {
                        const log = JSON.stringify(errorQueue);
                        // const subject = `Group Library - Exception`;
                        // const body = `Regarding: ${window.location.href}\nWhat I was doing:\n[describe steps to reproduce the issue]\nStack:` + log;
                        // window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
                        const blob = new Blob([log], { type: 'text/plan;charset=utf-8' });
                        saveAs(blob, 'log.txt', { autoBom: true });
                    }}
                />
                <DefaultButton text="OK"
                    styles={panelButtonsStyles}
                    onClick={() => {
                        setErrorQueue(t => {
                            const _t = [...t];

                            // remove first item on the queue;
                            _t.shift();

                            return _t;
                        });
                    }}
                />
            </DialogFooter>
        </Dialog>
    }

    const onRenderValidationDialog = () => {
        return <>
            {validationState && (validationState === EValidationState.Validating || validationState === EValidationState.PendingUser) &&
                <ValidationDialog
                    defaultValue={validation.current!}
                    loadingPageDuringValidation={true}
                    onCancel={() => {
                        validation.current!.state = EValidationState.Rejected;
                        setValidationState(validation.current!.state);
                    }}
                    onContinue={(e: IValidationModel) => {
                        validation.current = e;
                        validation.current!.state = EValidationState.Fulfilled;

                        validation.current.validationMessages.forEach(vm => {
                            vm.resolved = true;
                        });

                        SaveAsync(validation.current.data);
                    }}
                />
            }
        </>;
    }

    const onRenderContextMenu = () => {
        return <>
            {menuCoordinates &&
                <svg height="100" width="100" style={{ position: 'fixed', left: menuCoordinates.x + 'px', top: menuCoordinates.y + 'px' }}>
                    <circle cx="50" cy="50" r="40" stroke="black" stroke-width="3" fill="red" />
                </svg>
            }
        </>;
    }

    if (state.status.recycling)
        return <PageLoading />

    return (
        <ApplicationContext.Provider value={context}>
            {props.children}
            {onRenderValidationDialog()}
            {onRenderErrorDialog()}
            {onRenderContextMenu()}
        </ApplicationContext.Provider>
    );
}

export default ApplicationContextProvider;
export const useApplication = () => useContext(ApplicationContext);
